import React, { useEffect } from 'react';

import { Form, Formik, FormikConfig } from 'formik';
import { ChevronDown, Loader2 } from 'lucide-react';

import { cn } from 'components/lib/utils';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from 'components/ui/collapsible';

export interface CollapsibleSectionFormProps<T> {
  title: string;
  description?: string;
  defaultExpanded?: boolean;
  initialValues: T;
  validationSchema?: FormikConfig<T>['validationSchema'];
  onSubmit: FormikConfig<T>['onSubmit'];
  children: React.ReactNode;
  isLoading?: boolean;
  saveButtonText?: string;
  /** When true, children are not wrapped in the default 2-column grid (use for custom layouts). */
  disableFormGrid?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  enableReinitialize?: boolean;
}

const FormDirtySync = ({
  dirty,
  onDirtyChange
}: {
  dirty: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}) => {
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  return null;
};

export function CollapsibleSectionForm<T extends Record<string, unknown>>({
  title,
  description = '',
  defaultExpanded = false,
  initialValues,
  validationSchema,
  onSubmit,
  children,
  isLoading = false,
  saveButtonText = 'Save',
  disableFormGrid = false,
  onDirtyChange,
  enableReinitialize = true
}: CollapsibleSectionFormProps<T>): React.ReactElement {
  return (
    <Card className="w-full overflow-hidden rounded-lg border border-base-sidebarBorder bg-base-white shadow-none">
      <Collapsible defaultOpen={defaultExpanded} className="group w-full">
        <CardHeader className="p-0">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-9 text-left transition-colors hover:bg-base-backgroundHover/50 data-[state=open]:bg-transparent">
        <div className="flex min-h-[44px] flex-1 flex-col gap-1">
          <h4 className="font-sans text-xl font-semibold leading-7 tracking-[-0.4px] text-base-foreground">
            {title}
          </h4>
          {description && (
            <p className="font-sans text-base font-normal leading-6 text-base-mutedForeground">
              {description}
            </p>
          )}
        </div>
        <div className="flex size-14 shrink-0 items-center justify-center gap-3 py-3 px-6 text-base-mutedForeground">
          <ChevronDown className="size-6 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </div>
      </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent
        className={cn(
          'overflow-hidden',
          'data-[state=open]:animate-collapsible-down',
          'data-[state=closed]:animate-collapsible-up'
        )}
      >
        <CardContent className="px-9 pb-9 pt-0">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize={enableReinitialize}
          >
            {({ handleSubmit, isSubmitting, isValid, dirty }) => {
              const isSaveDisabled =
                !dirty || !isValid || isSubmitting || isLoading;
              return (
                <Form>
                  <FormDirtySync dirty={dirty} onDirtyChange={onDirtyChange} />
                  <div
                    className={
                      disableFormGrid
                        ? 'space-y-6'
                        : 'grid grid-cols-1 gap-x-9 gap-y-4 md:grid-cols-2'
                    }
                  >
                    {children}
                  </div>
                  <div className="mt-9 flex justify-end">
                    <Button
                      type="button"
                      variant="default"
                      onClick={() => handleSubmit()}
                      disabled={isSaveDisabled}
                      data-testid="collapsible-section-save"
                      className="h-10 rounded-md bg-base-primary px-4 py-2 font-medium text-base-primaryForeground shadow-none hover:bg-button-hover-default"
                    >
                      {isSubmitting || isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {saveButtonText}
                        </>
                      ) : (
                        saveButtonText
                      )}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </CollapsibleContent>
    </Collapsible>
    </Card>
  );
}
